import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Moon, Edit2, Save, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { getDailyActivation, getUserBirthData, updateUserBirthData } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { trackEvent } from "@/lib/useAnalytics";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";


import { getOrdinalSuffix, HOUSE_DATA, PLANET_DATA, NorthIndianChart, PlanetPopup, HousePopup } from "@/components/northIndianChart";

// Summary Panel
function SummaryPanel({ currentMoon, dasha, activatedPlanets }) {
  // Find the nakshatra ruler from activated planets
  const nakshatraRuler = activatedPlanets?.find(p => p.is_nakshatra_ruler);
  
  return (
    <div 
      className="rounded-2xl p-6 mb-6" 
      style={{ 
        background: "linear-gradient(135deg, rgba(15,15,18,0.9) 0%, rgba(8,8,12,0.9) 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.2)"
      }}
    >
      <h2 className="text-white/90 text-xl font-light mb-5 tracking-wide">Daily Chart Activation</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Current Moon */}
        <div className="flex items-start gap-4">
          <div className="text-4xl text-amber-400 drop-shadow-[0_0_8px_rgba(196,160,0,0.3)]">☾</div>
          <div>
            <p className="text-white/40 text-xs tracking-wider uppercase mb-1">Current Moon</p>
            <p className="text-white/90 text-lg">{currentMoon?.nakshatra}</p>
            <p className="text-white/50 text-sm">Pada {currentMoon?.pada} • {currentMoon?.sign}</p>
            <p className="text-white/30 text-xs mt-1">House {currentMoon?.house}</p>
          </div>
        </div>
        
        {/* Nakshatra Ruler */}
        <div className="flex items-start gap-4">
          <div className="text-3xl text-cyan-400 drop-shadow-[0_0_6px_rgba(34,211,238,0.4)]">
            {nakshatraRuler?.glyph || currentMoon?.nakshatra_ruler?.charAt(0)}
          </div>
          <div>
            <p className="text-white/40 text-xs tracking-wider uppercase mb-1">Nakshatra Lord</p>
            <p className="text-cyan-400 text-lg">{currentMoon?.nakshatra_ruler}</p>
            <p className="text-white/40 text-xs mt-1">Rules {currentMoon?.nakshatra}</p>
          </div>
        </div>
        
        {/* Dasha Period */}
        <div className="flex items-start gap-4">
          <div className="flex gap-1">
            <span className="text-3xl text-amber-400 drop-shadow-[0_0_6px_rgba(196,160,0,0.4)]">{dasha?.mahadasha?.glyph}</span>
            <span className="text-3xl text-violet-400 drop-shadow-[0_0_6px_rgba(139,92,246,0.4)]">{dasha?.antardasha?.glyph}</span>
          </div>
          <div>
            <p className="text-white/40 text-xs tracking-wider uppercase mb-1">Dasha Period</p>
            <p className="text-white/90">
              <span className="text-amber-400">{dasha?.mahadasha?.planet}</span>
              <span className="text-white/20 mx-1">/</span>
              <span className="text-violet-400">{dasha?.antardasha?.planet}</span>
            </p>
            <p className="text-white/30 text-xs mt-1">
              {dasha?.mahadasha?.years_remaining}y remaining
            </p>
          </div>
        </div>
        
        {/* Active Planets */}
        <div>
          <p className="text-white/40 text-xs tracking-wider uppercase mb-2">Active Today</p>
          <div className="flex flex-wrap gap-2">
            {activatedPlanets?.map((planet) => (
              <div 
                key={planet.planet} 
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm ${
                  planet.is_mahadasha ? "bg-amber-400/15 border border-amber-400/30 text-amber-400" :
                  planet.is_antardasha ? "bg-violet-400/15 border border-violet-400/30 text-violet-400" :
                  planet.is_nakshatra_ruler ? "bg-cyan-400/15 border border-cyan-400/30 text-cyan-400" :
                  "bg-white/5 border border-white/10 text-white/70"
                }`}
              >
                <span className="text-lg">{planet.glyph}</span>
                <span className="text-xs">{planet.planet}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Birth Data Form
function BirthDataForm({ initialData, onSave, onCancel }) {
  const [form, setForm] = useState({
    birth_date: initialData?.birth_date || "",
    birth_time: initialData?.birth_time || "",
    birth_city: initialData?.birth_city || "",
    birth_time_unknown: Boolean(initialData?.birth_time_unknown),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.birth_date || !form.birth_city || (!form.birth_time_unknown && !form.birth_time)) {
      toast.error("Please fill in all fields");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      toast.success("Birth data saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save birth data");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto rounded-2xl p-6"
      style={{ 
        background: "linear-gradient(135deg, rgba(15,15,18,0.95) 0%, rgba(8,8,12,0.95) 100%)",
        border: "1px solid rgba(255,255,255,0.06)" 
      }}
    >
      <h2 className="text-white/90 text-xl font-light mb-2">Enter Your Birth Details</h2>
      <p className="text-white/40 text-sm mb-6">
        Your birth data is needed to calculate your Ascendant and show personalized daily activations.
      </p>
      
      <div className="space-y-4">
        <div>
          <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Date</Label>
          <Input
            type="date"
            value={form.birth_date}
            onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white/90 focus:border-amber-400/50"
            data-testid="birth-date-input"
          />
        </div>
        <div>
          <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Time</Label>
          <Input
            type="time"
            value={form.birth_time}
            disabled={form.birth_time_unknown}
            onChange={(e) => setForm({ ...form, birth_time: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white/90 focus:border-amber-400/50 disabled:opacity-40"
            data-testid="birth-time-input"
          />
          <label className="flex items-center gap-2 mt-2 text-xs text-white/50 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.birth_time_unknown}
              onChange={(e) => setForm({ ...form, birth_time_unknown: e.target.checked, birth_time: e.target.checked ? "" : form.birth_time })}
              className="accent-amber-400"
              data-testid="birth-time-unknown-checkbox"
            />
            I don't know my birth time
          </label>
          {form.birth_time_unknown && (
            <p className="text-[11px] text-white/35 mt-1" data-testid="birth-time-unknown-note">
              Noon will be used. Ascendant and house placements will be approximate and omitted from your daily horoscope.
            </p>
          )}
        </div>
        <div>
          <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth City</Label>
          <CityAutocomplete
            placeholder="e.g., Mumbai, India"
            value={form.birth_city}
            onChange={(val) => setForm({ ...form, birth_city: val })}
            className="mt-1 bg-white/5 border-white/10 text-white/90 placeholder:text-white/20 focus:border-amber-400/50"
            testId="birth-city-input"
          />
        </div>
      </div>
      
      <div className="flex gap-3 mt-6">
        {onCancel && (
          <Button
            variant="ghost"
            onClick={onCancel}
            className="flex-1 text-white/50 hover:text-white/80 hover:bg-white/5"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-amber-400/20 hover:bg-amber-400/30 text-amber-400 border border-amber-400/30"
          data-testid="save-birth-data-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </motion.div>
  );
}

export default function DailyChartActivation() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [birthData, setBirthData] = useState(null);
  const [hasBirthData, setHasBirthData] = useState(false);
  const [activationData, setActivationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Popup state
  const [selectedPlanet, setSelectedPlanet] = useState(null);
  const [selectedHouse, setSelectedHouse] = useState(null);

  // Check auth
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please sign in to access Daily Chart Activation");
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  // Fetch birth data
  useEffect(() => {
    if (user) {
      getUserBirthData()
        .then((res) => {
          setHasBirthData(res.has_birth_data);
          if (res.has_birth_data) {
            setBirthData(res.data);
          }
        })
        .catch(() => setHasBirthData(false))
        .finally(() => setLoading(false));
    }
  }, [user]);

  // Fetch activation data
  const fetchActivation = useCallback(async () => {
    if (!hasBirthData) return;
    try {
      const data = await getDailyActivation();
      setActivationData(data);
      setLastRefresh(new Date());
      trackEvent("daily_chart_activation_open");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch activation data");
    }
  }, [hasBirthData]);

  useEffect(() => {
    if (hasBirthData) {
      fetchActivation();
    }
  }, [hasBirthData, fetchActivation]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!hasBirthData) return;
    const interval = setInterval(fetchActivation, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [hasBirthData, fetchActivation]);

  const handleSaveBirthData = async (data) => {
    const result = await updateUserBirthData(data);
    setBirthData(result.data);
    setHasBirthData(true);
    setIsEditing(false);
    const activation = await getDailyActivation();
    setActivationData(activation);
    setLastRefresh(new Date());
  };

  const handlePlanetClick = (planet) => {
    setSelectedPlanet(planet);
  };

  const handleHouseClick = (house, sign, symbol, planets) => {
    setSelectedHouse({ house, sign, symbol, planets, data: HOUSE_DATA[house] });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-amber-400/60 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors"
            data-testid="daily-activation-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Back</span>
          </Link>
          
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <button
                onClick={fetchActivation}
                className="text-white/30 hover:text-white/60 text-xs flex items-center gap-1 transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            )}
            {birthData && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="text-white/40 hover:text-white/70 text-xs"
                data-testid="edit-birth-data-btn"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-14 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Birth Data Form */}
        {(!hasBirthData || isEditing) && (
          <BirthDataForm
            initialData={birthData}
            onSave={handleSaveBirthData}
            onCancel={isEditing ? () => setIsEditing(false) : null}
          />
        )}

        {/* Main Content */}
        {hasBirthData && !isEditing && activationData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Summary Panel */}
            <SummaryPanel
              currentMoon={activationData.current_moon}
              dasha={activationData.dasha}
              activatedPlanets={activationData.activation?.activated_planets}
            />

            {/* Chart Section */}
            <div 
              className="rounded-2xl p-6 sm:p-8" 
              style={{ 
                background: "linear-gradient(135deg, rgba(12,12,15,0.95) 0%, rgba(6,6,8,0.95) 100%)",
                border: "1px solid rgba(255,255,255,0.05)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
              }}
            >
              {/* Ascendant Info */}
              <div className="text-center mb-6">
                <p className="text-amber-400/60 text-sm tracking-wider">
                  Ascendant in {activationData.ascendant?.ascendant_sign} {activationData.ascendant?.ascendant_symbol}
                </p>
                {activationData.birth_time_unknown && (
                  <p className="text-white/35 text-xs mt-2" data-testid="activation-time-unknown-notice">
                    Birth time unknown — chart calculated for 12:00 noon, so the ascendant and houses are approximate.
                  </p>
                )}
              </div>
              
              {/* Interactive Chart */}
              <NorthIndianChart
                houses={activationData.houses}
                activatedPlanets={activationData.activation?.activated_planets}
                currentMoon={activationData.current_moon}
                onPlanetClick={handlePlanetClick}
                onHouseClick={handleHouseClick}
              />
              
              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-white/5">
                <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-400/50 shadow-[0_0_8px_rgba(196,160,0,0.5)]"></span>
                    <span className="text-white/40">Mahadasha</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-violet-400/50"></span>
                    <span className="text-white/40">Antardasha</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-cyan-400/50"></span>
                    <span className="text-white/40">Nakshatra Lord</span>
                  </span>
                </div>
                <p className="text-center text-white/20 text-xs mt-3">Tap houses or planets for details</p>
              </div>
            </div>

            {/* Activation Insights */}
            <div className="mt-8">
              <h3 className="text-white/50 text-xs tracking-wider uppercase mb-4">Today's Activations</h3>
              <div className="space-y-4">
                {activationData.activation?.activated_planets?.map((planet, idx) => (
                  <motion.div
                    key={planet.planet}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`rounded-xl p-5 cursor-pointer transition-all hover:translate-x-1 ${
                      planet.is_mahadasha ? "border-l-4 border-amber-400/60" :
                      planet.is_antardasha ? "border-l-4 border-violet-400/60" :
                      planet.is_nakshatra_ruler ? "border-l-4 border-cyan-400/60" :
                      "border-l-4 border-white/10"
                    }`}
                    style={{ background: "rgba(255,255,255,0.02)" }}
                    onClick={() => handlePlanetClick(planet)}
                  >
                    <div className="flex items-start gap-4">
                      <span className={`text-4xl flex-shrink-0 ${
                        planet.is_mahadasha ? "text-amber-400 drop-shadow-[0_0_10px_rgba(196,160,0,0.6)]" : 
                        planet.is_antardasha ? "text-violet-400 drop-shadow-[0_0_8px_rgba(167,139,250,0.5)]" : 
                        planet.is_nakshatra_ruler ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]" :
                        "text-white/80"
                      }`}>
                        {planet.glyph}
                      </span>
                      <div className="flex-1 min-w-0">
                        {/* Header with planet name and tags */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <p className="text-white font-medium text-lg">{planet.planet}</p>
                          <span className="text-white/30">in</span>
                          <p className="text-white/70">
                            {planet.house}{getOrdinalSuffix(planet.house)} House
                            {planet.house_sign && ` — ${planet.house_sign}`}
                          </p>
                          <div className="flex gap-1.5 ml-auto">
                            {planet.is_mahadasha && (
                              <span className="text-amber-400/90 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/30">
                                Mahadasha
                              </span>
                            )}
                            {planet.is_antardasha && (
                              <span className="text-violet-400/90 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-violet-400/15 border border-violet-400/30">
                                Antardasha
                              </span>
                            )}
                            {planet.is_nakshatra_ruler && (
                              <span className="text-cyan-400/90 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/30">
                                Nakshatra Lord
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Description/reason */}
                        <p className="text-white/40 text-xs mb-2 italic">{planet.description}</p>
                        
                        {/* Detailed insight */}
                        <p className="text-white/60 text-sm leading-relaxed">{planet.insight}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading state */}
        {hasBirthData && !isEditing && !activationData && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 text-amber-400/60 animate-spin" />
            <span className="text-white/40 ml-3">Calculating your chart...</span>
          </div>
        )}
      </main>

      {/* Popups */}
      <AnimatePresence>
        {selectedPlanet && (
          <PlanetPopup 
            planet={selectedPlanet} 
            planetInfo={PLANET_DATA[selectedPlanet.planet]}
            onClose={() => setSelectedPlanet(null)} 
          />
        )}
        {selectedHouse && (
          <HousePopup
            houseData={selectedHouse.data}
            sign={selectedHouse.sign}
            symbol={selectedHouse.symbol}
            planets={selectedHouse.planets}
            onClose={() => setSelectedHouse(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
